import { createContext, useContext, type ReactNode } from 'react'

import { cn } from '@/components/base'

type Variant = 'default' | 'highlight' | 'tiles'

const GroupVariant = createContext<Variant>('default')

/** How the group around a `LabelValue` lays it out. */
export const useLabelValueVariant = () => useContext(GroupVariant)

/**
 * A row of label/value pairs.
 *
 * `highlight` puts them on their own panel, for a summary that has to stand
 * out from the fields around it. `tiles` frames each pair as a tile of its
 * own with a large figure — the headline numbers a dashboard opens with —
 * and wraps them into as many columns as fit.
 */
export function LabelValueGroup({
  children,
  variant = 'default',
  title,
  className,
}: {
  children: ReactNode
  variant?: Variant
  title?: string
  className?: string
}) {
  const body =
    variant === 'tiles' ? (
      <div
        className={cn(
          'grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3',
          className,
        )}
      >
        {children}
      </div>
    ) : variant === 'highlight' ? (
      <div className={cn('mt-2 rounded-lg bg-line-soft p-4', className)}>
        {title ? <p className="mb-3 font-medium">{title}</p> : null}
        <div className="flex flex-row flex-wrap gap-6">{children}</div>
      </div>
    ) : (
      <div className={cn('flex flex-row flex-wrap gap-8', className)}>
        {children}
      </div>
    )

  return <GroupVariant.Provider value={variant}>{body}</GroupVariant.Provider>
}
